import React, { useContext, useState } from "react"
import { toast } from "react-toastify"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCopy, faUserGroup } from "@fortawesome/free-solid-svg-icons"
import Switch from "../atoms/Switch"
import { theme } from "../settings/config"
import { notebookContext } from "../providers/notebook.provider"
import { profileContext } from "../providers/profile.provider"
import { addNotebookEntry, setNotebookEntryShared } from "../utils/firestore.utils"
import { networkAwareMessage } from "../utils/network.utils"
import {
	NOTEBOOK_EXERCISE,
	NOTEBOOK_LEVELS,
	copyOfEntry,
	getNotebookLevel,
	isMine,
	isSharedIn,
	levelLabel,
} from "../utils/notebook.utils"

// ── Le varianti e la condivisione, nei pezzi che servono a tutti ──
// La riga del quaderno, la pagina di un esercizio, il foglio per cambiarlo
// e il programma di una lezione mostrano le varianti allo stesso modo: il
// livello col pallino del colore della palla (il SAT) o il numero (il PTR),
// in pastiglie da scorrere di lato.

const c = theme.colors

// Il livello: il pallino, se è del SAT, e il nome
export const LevelMark = ({ levelID, dotSize = 8 }) => {
	const level = getNotebookLevel(levelID)
	return (
		<>
			{level?.color && (
				<span
					aria-hidden="true"
					style={{
						width: dotSize,
						height: dotSize,
						borderRadius: "50%",
						flex: "none",
						background: c[level.color],
					}}
				/>
			)}
			{levelLabel(levelID)}
		</>
	)
}

// I livelli di una voce, in piccolo accanto al nome. Una voce senza livelli
// non ne ha; con tanti, i primi tre e quanti altri. `highlight` è quello
// della pastiglia scelta.
export function LevelTags({ entry, highlight = null, ground = c.background }) {
	const levels = (entry.variants ?? []).map(variant => variant.level).filter(Boolean)
	if (levels.length === 0) return null
	const shown = levels.slice(0, 3)
	return (
		<span
			aria-label={`Varianti: ${levels.map(levelLabel).join(", ")}`}
			style={{ display: "flex", flex: "none", gap: "0.25rem", alignItems: "center" }}>
			{shown.map(levelID => {
				const isHit = levelID === highlight
				return (
					<span
						key={levelID}
						aria-hidden="true"
						style={{
							display: "inline-flex",
							alignItems: "center",
							gap: "0.25rem",
							height: "1.375rem",
							padding: "0 0.5rem",
							borderRadius: "11px",
							background: isHit ? c.gunmetal_fill : ground,
							color: isHit ? c.white : c.gunmetal,
							fontSize: "0.6875rem",
							fontWeight: 600,
						}}>
						<LevelMark levelID={levelID} dotSize={7} />
					</span>
				)
			})}
			{levels.length > shown.length && (
				<span aria-hidden="true" style={{ fontSize: "0.6875rem", color: c.text_light }}>
					+{levels.length - shown.length}
				</span>
			)}
		</span>
	)
}

// Il segno di una voce condivisa, da fuori: le due sagome in una pastiglia
// tonda, accanto ai livelli. Una voce senza è solo tua; di un collega lo
// dice il suo nome.
export const SharedBadge = ({ entry }) => (
	<span
		role="img"
		aria-label={entry?.kind === NOTEBOOK_EXERCISE ? "Condiviso con i colleghi" : "Condivisa con i colleghi"}
		style={{
			flex: "none",
			display: "inline-flex",
			alignItems: "center",
			justifyContent: "center",
			width: "1.375rem",
			height: "1.375rem",
			borderRadius: "50%",
			background: c.gunmetal_lighter,
			color: c.gunmetal,
			fontSize: "0.625rem",
		}}>
		<FontAwesomeIcon icon={faUserGroup} />
	</span>
)

// Una pastiglia: piena se scelta, sul fondo `ground` se no
export const levelPill = (isSelected, ground) => ({
	flex: "none",
	display: "inline-flex",
	alignItems: "center",
	gap: "0.375rem",
	height: "2.125rem",
	padding: "0 0.875rem",
	border: 0,
	borderRadius: "17px",
	fontFamily: "inherit",
	fontSize: "0.8125rem",
	fontWeight: 600,
	cursor: "pointer",
	color: isSelected ? c.white : c.gunmetal,
	background: isSelected ? c.gunmetal_fill : ground,
	transition: "background 0.15s, color 0.15s",
})

// Le varianti di una voce, da scorrere di lato, e "+ Variante" in fondo
// per chi la può cambiare. Una voce con una variante sola e senza livello
// non ha niente da scegliere: resta solo "+ Variante".
export function VariantTabs({ entry, selected, onSelect, onAdd, ground = c.background, bleed = "1rem" }) {
	const variants = entry.variants ?? []
	const showTabs = variants.length > 1 || Boolean(variants[0]?.level)
	if (!showTabs && !onAdd) return null
	return (
		<div
			role="tablist"
			aria-label="Varianti"
			style={{
				display: "flex",
				gap: "0.375rem",
				margin: `0 -${bleed}`,
				padding: `0 ${bleed}`,
				overflowX: "auto",
				scrollbarWidth: "none",
			}}>
			{showTabs &&
				variants.map(variant => {
					const isSelected = variant.variantID === selected
					return (
						<button
							key={variant.variantID}
							type="button"
							role="tab"
							aria-selected={isSelected}
							onClick={() => onSelect(variant.variantID)}
							style={levelPill(isSelected, ground)}>
							<LevelMark levelID={variant.level} />
						</button>
					)
				})}
			{onAdd && (
				<button
					type="button"
					onClick={onAdd}
					style={{
						...levelPill(false, "transparent"),
						boxShadow: `inset 0 0 0 1.5px ${c.gunmetal_light}`,
					}}>
					+ Variante
				</button>
			)}
		</div>
	)
}

// "Rispetto alle altre: …", la riga che dice cosa cambia. Con una variante
// sola non c'è niente con cui confrontarla.
export function VariantNote({ entry, variant }) {
	if (!variant?.note || (entry.variants ?? []).length < 2) return null
	return (
		<p style={{ margin: 0, fontSize: "0.8125rem", lineHeight: 1.4, color: c.text_light }}>
			<span style={{ fontWeight: 600, color: c.gunmetal }}>Rispetto alle altre:</span>{" "}
			{variant.note}
		</p>
	)
}

// I livelli fra cui scegliere, quelli del SAT e poi quelli del PTR. Quelli
// che hanno già una variante sono sbiaditi e non si toccano; toccare quello
// scelto lo toglie, se il livello si può anche non dire.
export function LevelChips({ value, used = [], onChange, canClear = true, disabled, ground = "transparent" }) {
	const taken = new Set(used.filter(Boolean))
	const groups = [
		NOTEBOOK_LEVELS.filter(level => level.color),
		NOTEBOOK_LEVELS.filter(level => !level.color),
	]
	return (
		<div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
			{groups.map((levels, i) => (
				<div key={i} role="group" aria-label={i === 0 ? "Livelli SAT" : "Livelli PTR"} style={{ display: "flex", flexWrap: "wrap", gap: "0.375rem" }}>
					{levels.map(level => {
						const isSelected = value === level.id
						const isTaken = taken.has(level.id) && !isSelected
						return (
							<button
								key={level.id}
								type="button"
								aria-pressed={isSelected}
								disabled={disabled || isTaken}
								title={isTaken ? "Ha già la sua variante" : undefined}
								onClick={() => onChange(isSelected && canClear ? null : level.id)}
								style={{
									...levelPill(isSelected, ground),
									boxShadow: isSelected || ground !== "transparent" ? "none" : `inset 0 0 0 2px ${c.surface}`,
									opacity: isTaken ? 0.35 : 1,
									cursor: isTaken ? "default" : "pointer",
								}}>
								<LevelMark levelID={level.id} />
							</button>
						)
					})}
				</div>
			))}
		</div>
	)
}

// Un riquadro sul fondo `ground`, come gli altri blocchi del foglio
const block = ground => ({
	display: "flex",
	alignItems: "center",
	gap: "0.75rem",
	padding: "0.625rem 0.75rem 0.625rem 0.875rem",
	borderRadius: "15px",
	background: ground,
})

// Condividere coi colleghi del circolo che si guarda, per chi l'ha scritta;
// per un collega, di chi è
export function NotebookShare({ entry, ground = c.background }) {
	const { schoolID, schoolName } = useContext(notebookContext)
	const [isSaving, setSaving] = useState(false)
	const exercise = entry.kind === NOTEBOOK_EXERCISE

	if (!isMine(entry))
		return (
			<div style={block(ground)}>
				<span
					aria-hidden="true"
					style={{
						flex: "none",
						width: "2rem",
						height: "2rem",
						borderRadius: "50%",
						background: c.gunmetal_lighter,
						color: c.gunmetal,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						fontSize: "0.75rem",
						fontWeight: 700,
					}}>
					{initials(entry.authorName)}
				</span>
				<span style={{ flex: 1, minWidth: 0 }}>
					<span style={{ display: "block", fontSize: "0.875rem", fontWeight: 600, color: c.gunmetal }}>
						Di {entry.authorName || "un collega"}
					</span>
					<span style={{ display: "block", fontSize: "0.75rem", lineHeight: 1.35, color: c.text_light }}>
						{exercise
							? "Condiviso con i colleghi: lo puoi usare, lo cambia solo chi l'ha scritto"
							: "Condivisa con i colleghi: la puoi usare, la cambia solo chi l'ha scritta"}
					</span>
				</span>
			</div>
		)

	if (!schoolID) return null
	const isShared = isSharedIn(entry, schoolID)

	const toggle = async () => {
		if (isSaving) return
		setSaving(true)
		try {
			await setNotebookEntryShared(entry.entryID, schoolID, !isShared)
		} catch (error) {
			const message = networkAwareMessage(error, "Non sono riuscito a cambiarlo")
			toast.error(message, { toastId: message, position: "top-center" })
		}
		setSaving(false)
	}

	return (
		<div
			role="switch"
			aria-checked={isShared}
			aria-label="Condividi con i colleghi"
			tabIndex={0}
			onClick={toggle}
			onKeyDown={event => {
				if (event.key !== "Enter" && event.key !== " ") return
				event.preventDefault()
				toggle()
			}}
			style={{ ...block(ground), cursor: "pointer" }}>
			<FontAwesomeIcon icon={faUserGroup} color={c.gunmetal} />
			<span style={{ flex: 1, minWidth: 0 }}>
				<span style={{ display: "block", fontSize: "0.875rem", fontWeight: 600, color: c.gunmetal }}>
					Condividi con i colleghi
				</span>
				<span style={{ display: "block", fontSize: "0.75rem", lineHeight: 1.35, color: c.text_light }}>
					{isShared
						? `${schoolName ? `Di ${schoolName}` : "Del circolo"}: ${exercise ? "lo vedono e lo usano, ma lo cambi solo tu" : "la vedono e la usano, ma la cambi solo tu"}`
						: exercise
							? "Solo tuo, per adesso"
							: "Solo tua, per adesso"}
				</span>
			</span>
			<Switch
				shadow="0"
				m="0"
				checked={isShared}
				isLoading={isSaving}
				activeColor={c.gunmetal_fill}
				inactiveColor="gray_light"
				style={{ flexShrink: 0 }}
			/>
		</div>
	)
}

const initials = name =>
	(name ?? "")
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map(word => word[0].toUpperCase())
		.join("") || "?"

// "Copia nel mio quaderno", per una voce di un collega: tutte le sue
// varianti, nel proprio quaderno, non condivisa
export function useCopyToNotebook() {
	const { coach } = useContext(profileContext)
	const [isCopying, setCopying] = useState(false)
	const copy = async entry => {
		if (isCopying || !coach?.coachID) return
		setCopying(true)
		try {
			await addNotebookEntry(coach.coachID, copyOfEntry(entry, null, {}))
			toast.success(
				entry.kind === NOTEBOOK_EXERCISE ? "Copiato nel tuo quaderno" : "Copiata nel tuo quaderno",
				{ position: "top-center" },
			)
		} catch (error) {
			const message = networkAwareMessage(error, "Non sono riuscito a copiarla")
			toast.error(message, { toastId: message, position: "top-center" })
		}
		setCopying(false)
	}
	return { copy, isCopying }
}

export const CopyIcon = () => <FontAwesomeIcon icon={faCopy} />
